import {
  type LoginSecurityAlert,
  familyAccountPasswordReset,
  loginSecurityAlertIgnore,
  loginSecurityAlertsList,
} from '@chorepop/core/client';
import { type Component, Show, createResource, createSignal } from 'solid-js';
import { GuardianChallengeDialog } from './GuardianChallengeDialog';

type Props = {
  sessionId: string;
  guardianAccountId: number;
};

export const LoginSecurityAlertDialog: Component<Props> = (props) => {
  const [alerts, { refetch }] = createResource(
    () => props.sessionId,
    async (sessionId) => {
      const result = await loginSecurityAlertsList({ sessionId });
      return result.ok ? result.value : [];
    },
  );
  const [challengeOpen, setChallengeOpen] = createSignal(false);
  const [settingsToken, setSettingsToken] = createSignal<string | null>(null);
  const [newPassword, setNewPassword] = createSignal('');
  const [confirmPassword, setConfirmPassword] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);

  const current = () => alerts()?.[0];

  const clearActionState = () => {
    setSettingsToken(null);
    setNewPassword('');
    setConfirmPassword('');
    setErrorMessage(null);
  };

  const ignore = async (alert: LoginSecurityAlert) => {
    setBusy(true);
    setErrorMessage(null);
    const result = await loginSecurityAlertIgnore({
      sessionId: props.sessionId,
      loginSecurityAlertId: alert.loginSecurityAlertId,
    });
    setBusy(false);
    if (!result.ok) {
      setErrorMessage(result.error.message);
      return;
    }
    clearActionState();
    await refetch();
  };

  const resetPassword = async (event: Event, alert: LoginSecurityAlert) => {
    event.preventDefault();
    const token = settingsToken();
    if (!token) return;
    if (newPassword().length < 10) {
      setErrorMessage('Password must be at least 10 characters.');
      return;
    }
    if (newPassword() !== confirmPassword()) {
      setErrorMessage('The two passwords do not match.');
      return;
    }
    setBusy(true);
    setErrorMessage(null);
    const result = await familyAccountPasswordReset({
      sessionId: props.sessionId,
      settingsToken: token,
      loginSecurityAlertId: alert.loginSecurityAlertId,
      newPassword: newPassword(),
    });
    setBusy(false);
    if (!result.ok) {
      setErrorMessage(result.error.message);
      return;
    }
    clearActionState();
    await refetch();
  };

  return (
    <>
      <Show when={current()} keyed>
        {(alert) => (
          <div class="confirm-backdrop">
            <section
              class="confirm-dialog login-security-alert-dialog"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="login-security-alert-title"
              data-testid="login-security-alert"
            >
              <span class="mobile-home-eyebrow">Security warning</span>
              <h2 id="login-security-alert-title">Someone failed to sign in</h2>
              <p>
                There were 3 unsuccessful {alert.credentialKind} attempts for{' '}
                <strong>{alert.targetDisplayName}</strong> on this device. That{' '}
                {alert.credentialKind} is temporarily locked.
              </p>
              <Show
                when={settingsToken()}
                fallback={
                  <div class="confirm-actions">
                    <button
                      type="button"
                      class="ghost"
                      disabled={busy()}
                      onClick={() => void ignore(alert)}
                    >
                      Ignore
                    </button>
                    <button
                      type="button"
                      class="primary"
                      disabled={busy()}
                      onClick={() => setChallengeOpen(true)}
                    >
                      Change password
                    </button>
                  </div>
                }
              >
                <form class="action-form" onSubmit={(event) => void resetPassword(event, alert)}>
                  <p>
                    Set a new password for {alert.targetDisplayName}. Existing sessions and device
                    PINs for that account will be removed.
                  </p>
                  <label class="field">
                    <span class="field-label">New password</span>
                    <input
                      type="password"
                      autocomplete="new-password"
                      value={newPassword()}
                      disabled={busy()}
                      onInput={(event) => setNewPassword(event.currentTarget.value)}
                    />
                  </label>
                  <label class="field">
                    <span class="field-label">Confirm new password</span>
                    <input
                      type="password"
                      autocomplete="new-password"
                      value={confirmPassword()}
                      disabled={busy()}
                      onInput={(event) => setConfirmPassword(event.currentTarget.value)}
                    />
                  </label>
                  <div class="confirm-actions">
                    <button
                      type="button"
                      class="ghost"
                      disabled={busy()}
                      onClick={clearActionState}
                    >
                      Cancel
                    </button>
                    <button type="submit" class="primary" disabled={busy()}>
                      {busy() ? 'Changing…' : 'Change password'}
                    </button>
                  </div>
                </form>
              </Show>
              <Show when={errorMessage()}>
                {(message) => (
                  <div class="form-error" role="alert">
                    {message()}
                  </div>
                )}
              </Show>
            </section>
          </div>
        )}
      </Show>

      <Show when={challengeOpen()}>
        <GuardianChallengeDialog
          guardianAccountId={props.guardianAccountId}
          onCancel={() => setChallengeOpen(false)}
          onSuccess={(token) => {
            setChallengeOpen(false);
            setSettingsToken(token);
          }}
        />
      </Show>
    </>
  );
};
